/**
 * Magic-link approval result page.
 *
 * Reads the `?t=<token>` query param, hits /api/approvals/quick/{token}, and
 * renders a friendly result card. No auth cookie required.
 */
import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Warning, Clock } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function QuickApprovalPage({ defaultAction }) {
    const { search } = useLocation();
    const token = new URLSearchParams(search).get("t");
    const [state, setState] = useState({ loading: true, ok: false, data: null, error: null });

    useEffect(() => {
        let alive = true;
        (async () => {
            if (!token) {
                setState({ loading: false, ok: false, error: "Missing token" });
                return;
            }
            try {
                const { data } = await axios.get(`${BASE}/api/approvals/quick/${encodeURIComponent(token)}`);
                if (!alive) return;
                setState({ loading: false, ok: true, data });
            } catch (err) {
                if (!alive) return;
                setState({
                    loading: false, ok: false,
                    error: err?.response?.data?.detail || err.message || "Unable to process request",
                    status: err?.response?.status,
                });
            }
        })();
        return () => { alive = false; };
    }, [token]);

    const action = state?.data?.action || defaultAction;
    let icon = <Clock size={56} weight="duotone" className="text-muted-foreground" />;
    let title = "Processing…";
    let body = "Verifying your approval link with FormFlo.";
    let tone = "text-foreground";

    if (!state.loading) {
        if (state.ok) {
            if (state.data.status === "already_actioned") {
                icon = <Warning size={56} weight="duotone" className="text-yellow-600" />;
                title = "Already actioned";
                body = `This record has already been ${state.data.final_status === 'approved' ? 'approved' : state.data.final_status === 'rejected' ? 'rejected' : 'closed'}.`;
            } else if (state.data.status === "step_moved") {
                icon = <Warning size={56} weight="duotone" className="text-yellow-600" />;
                title = "Workflow has moved on";
                body = "This record has progressed beyond the step this link was issued for. Open FormFlo to review the latest state.";
            } else if (state.data.status === "done") {
                const verb = state.data.action === "approve" ? "Approved" : "Rejected";
                icon = action === "approve"
                    ? <CheckCircle size={56} weight="duotone" className="text-green-600" />
                    : <XCircle size={56} weight="duotone" className="text-destructive" />;
                title = `${verb} · ${state.data.form_title || "Record"}`;
                body = `Decision recorded. The submitter will be notified by email.`;
                tone = action === "approve" ? "text-green-700" : "text-destructive";
            }
        } else {
            icon = <XCircle size={56} weight="duotone" className="text-destructive" />;
            title = state.status === 410 ? "Link expired" : state.status === 403 ? "Not authorised" : "Unable to process";
            body = state.error || "This link may be invalid or no longer applicable.";
            tone = "text-destructive";
        }
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
            <div className="brut-section p-12 max-w-lg w-full text-center" data-testid="quick-approval-card">
                <div className="flex justify-center mb-6">{icon}</div>
                <div className="label-overline mb-2">FormFlo · One-click decision</div>
                <h1 className={`text-3xl font-display font-bold tracking-tight mb-3 ${tone}`} data-testid="quick-approval-title">
                    {title}
                </h1>
                <p className="text-sm text-muted-foreground mb-8" data-testid="quick-approval-body">{body}</p>
                <Link
                    to="/portal"
                    className="inline-block rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 text-sm font-medium"
                    data-testid="open-formflo-btn"
                >
                    Open FormFlo
                </Link>
            </div>
        </div>
    );
}
